// Draggable + collapsible lead-workspace sections (Edit.cshtml).
// Each section is `<section data-section="key">` inside `#lead-sections`, with a drag handle
// (`[data-drag-handle]`) and a collapse toggle (`[data-collapse]`). Order + collapsed state are
// persisted per-browser in localStorage so an agent's preferred layout sticks across leads.

const STORAGE_KEY = "crm.leadLayout.v1";

interface LayoutState {
  order: string[];
  collapsed: Record<string, boolean>;
}

function load(): LayoutState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const s = JSON.parse(raw) as Partial<LayoutState>;
      return { order: s.order ?? [], collapsed: s.collapsed ?? {} };
    }
  } catch {
    /* ignore corrupt/blocked storage */
  }
  return { order: [], collapsed: {} };
}

function persist(container: HTMLElement): void {
  const sections = Array.from(
    container.querySelectorAll<HTMLElement>(":scope > [data-section]"),
  );
  const state: LayoutState = {
    order: sections.map((s) => s.dataset.section!).filter(Boolean),
    collapsed: {},
  };
  for (const s of sections) {
    const key = s.dataset.section!;
    if (s.classList.contains("sec-collapsed")) state.collapsed[key] = true;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage may be unavailable (private mode) — layout just won't persist */
  }
}

/** The section the pointer is currently above (for insert-before), or null to append. */
function sectionAfterPoint(container: HTMLElement, y: number, dragging: HTMLElement): HTMLElement | null {
  const others = Array.from(
    container.querySelectorAll<HTMLElement>(":scope > [data-section]"),
  ).filter((s) => s !== dragging);

  for (const s of others) {
    const box = s.getBoundingClientRect();
    if (y < box.top + box.height / 2) return s;
  }
  return null;
}

/** The call thread runs oldest-first, so it has to open on the newest message at the bottom.
 *  A collapsed section has no layout and scrollTop will not stick, hence the repeat on expand. */
function scrollChatToEnd(): void {
  document
    .querySelectorAll<HTMLElement>(".chat-thread")
    .forEach((el) => { el.scrollTop = el.scrollHeight; });
}

export function initLeadSections(): void {
  const container = document.getElementById("lead-sections");
  if (!container) return;

  const sections = Array.from(
    container.querySelectorAll<HTMLElement>(":scope > [data-section]"),
  );
  if (sections.length === 0) return;

  const state = load();

  // Apply saved order: append known sections first (in saved order), leaving any new/unknown
  // section in its original relative position at the end.
  for (const key of state.order) {
    const el = sections.find((s) => s.dataset.section === key);
    if (el) container.appendChild(el);
  }
  // Apply saved collapsed state.
  for (const s of sections) {
    if (state.collapsed[s.dataset.section!]) s.classList.add("sec-collapsed");
  }

  // Collapse toggles.
  container.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-collapse]");
    if (!btn) return;
    const section = btn.closest<HTMLElement>("[data-section]");
    if (!section) return;
    section.classList.toggle("sec-collapsed");
    if (!section.classList.contains("sec-collapsed")) scrollChatToEnd();
    persist(container);
  });

  // Drag to reorder — only the handle is draggable, so inputs stay selectable.
  let dragging: HTMLElement | null = null;

  container.querySelectorAll<HTMLElement>("[data-drag-handle]").forEach((handle) => {
    handle.setAttribute("draggable", "true");

    handle.addEventListener("dragstart", (e) => {
      dragging = handle.closest<HTMLElement>("[data-section]");
      if (!dragging) return;
      dragging.classList.add("sec-dragging");
      e.dataTransfer?.setData("text/plain", dragging.dataset.section ?? "");
      if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
    });

    handle.addEventListener("dragend", () => {
      dragging?.classList.remove("sec-dragging");
      container
        .querySelectorAll(".sec-over")
        .forEach((s) => s.classList.remove("sec-over"));
      dragging = null;
    });
  });

  container.addEventListener("dragover", (e) => {
    if (!dragging) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";

    const after = sectionAfterPoint(container, e.clientY, dragging);
    container
      .querySelectorAll(".sec-over")
      .forEach((s) => s.classList.remove("sec-over"));
    if (after) after.classList.add("sec-over");

    if (after == null) container.appendChild(dragging);
    else if (after !== dragging.nextElementSibling) container.insertBefore(dragging, after);
  });

  container.addEventListener("drop", (e) => {
    if (!dragging) return;
    e.preventDefault();
    persist(container);
  });

  scrollChatToEnd();
}
